import './template-entry-form.scss';

import { useMutation } from '@apollo/client';
import { filter, kebabCase, omit } from 'lodash';
import { useCallback, useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { v4 as uuid } from 'uuid';

import { useAppContext } from '../../data/context/app-context';
import { useTemplateEntryTimeValues } from '../../data/data/templates/select-values';
import { TemplateEntryItem } from '../../data/data/types';
import {
  Honorific,
  TemplateEntryItemDataFragment,
  TemplateEntryType,
  ValidateTemplateEntryItemsDocument,
} from '../../data/types/api/graphql';
import {
  formatMinutesToTime,
  formatTimeToMinutes,
  formatTimeToTimeSpan,
} from '../../data/utils/date.utils';
import { AlertMessageType } from '../alert-message/alert-message';
import { Button, ButtonIcon, ButtonType } from '../button/button';
import { InputFieldController } from '../form/controllers/input-field';
import { InputSelectController } from '../form/controllers/input-select';
import { FormColumns, FormRow } from '../form/form';
import { CustomFormattedMessage } from '../intl/custom-formatted-message';
import { Message, MessageType } from '../message/message';
import { useTemplateEntryForm } from './form/use-template-entry-form';

type TemplateEntryFormProps = {
  entries: TemplateEntryItemDataFragment[];
  templateId?: string | null;
  healthProviderId: string;
  honorific: Honorific;
  blockSize: number;
  onSave?: (value: TemplateEntryItem) => void;
  onDirty?: (status: boolean) => void;
  overwrite?: boolean | null;
};

export function TemplateEntryForm({
  entries,
  templateId,
  healthProviderId,
  honorific,
  blockSize,
  onSave,
  onDirty,
  overwrite,
}: TemplateEntryFormProps) {
  const { showAlertMessage } = useAppContext();

  const { form, formIsValid, setErrors } = useTemplateEntryForm(blockSize);

  const { data: entryTimeData } = useTemplateEntryTimeValues(
    blockSize,
    entries.length === 0
  );

  const type = form.watch('type');
  const startTime = form.watch('startTime');
  const endTime = form.watch('endTime');
  const minutes = form.watch('minutes');

  const onHandleStartTime = useCallback(
    (value: string) => {
      if (value) {
        const formattedStartTime = formatTimeToMinutes(value);

        form.setValue(
          'endTime',
          formatMinutesToTime(formattedStartTime + minutes),
          {
            shouldDirty: true,
          }
        );
      }
    },
    [form, minutes]
  );

  useEffect(() => {
    if (startTime && endTime) {
      let appointments = Math.floor(
        (formatTimeToMinutes(endTime) - formatTimeToMinutes(startTime)) /
          minutes
      );

      if (minutes === 0) {
        appointments = 0;
      }

      if (appointments < 0) {
        appointments = 0;
      }

      form.setValue('appointments', appointments, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [form, startTime, endTime, minutes]);

  useEffect(() => {
    if (minutes === 0) {
      form.setValue('type', TemplateEntryType.Exception, {
        shouldDirty: true,
        shouldValidate: true,
      });
    } else {
      form.setValue('type', TemplateEntryType.AppointmentBlock, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [form, minutes, type]);

  useEffect(() => {
    onDirty?.(form.formState.isDirty);
  }, [form.formState.isDirty, onDirty]);

  const [
    validateTemplateEntryItems,
    { loading: validateTemplateEntryItemsLoading },
  ] = useMutation(ValidateTemplateEntryItemsDocument);

  const onHandleAdd = useCallback(async () => {
    const values = form.getValues();

    const value = {
      id: `temp-id-${uuid()}`,
      name: values.name,
      type: values.type,
      minuteStartTime: formatTimeToTimeSpan(values.startTime),
      minuteEndTime: formatTimeToTimeSpan(values.endTime),
      minuteLength: values.minutes,
      appointments: [],
    };

    try {
      const result = await validateTemplateEntryItems({
        variables: {
          honorific,
          healthProviderId,
          templateId: templateId ?? null,
          entry: omit(value, 'id'),
          overWriteConsultTypeElse: overwrite ?? false,
          entries: entries.map(entry =>
            omit(
              {
                ...entry,
                id: entry.id.includes('temp-id') ? null : entry.id,
                minuteLength: entry.minuteLength ?? 0,
                appointments: entry.appointments.map(appointment =>
                  omit(
                    {
                      ...appointment,
                      id: appointment.id.includes('temp-id')
                        ? null
                        : appointment.id,
                      minuteLength: appointment.minuteLength ?? 0,
                    },
                    '__typename',
                    'appointments'
                  )
                ),
              },
              '__typename'
            )
          ),
        },
      });

      if (!result.data?.templateValidateEntries?.errors) {
        onSave?.(value);

        form.reset();
      } else {
        setErrors(result.data?.templateValidateEntries?.errors);
      }
    } catch (error) {
      showAlertMessage({
        type: AlertMessageType.ERROR,
        message: (error as Error).message,
      });
    }
  }, [
    entries,
    form,
    healthProviderId,
    honorific,
    onSave,
    overwrite,
    setErrors,
    showAlertMessage,
    templateId,
    validateTemplateEntryItems,
  ]);

  return (
    <FormProvider {...form}>
      <div
        className={`template-entry-form template-entry-form--${kebabCase(
          type
        )}`}
      >
        <div className="template-entry-form__content">
          <FormColumns>
            <FormRow width="25%">
              <InputFieldController
                type="time"
                name="startTime"
                label={
                  <>
                    <CustomFormattedMessage id="template.entry.start-time" />
                    <small>*</small>
                  </>
                }
                onChange={onHandleStartTime}
              />
            </FormRow>

            <FormRow width="25%">
              <InputFieldController
                type="time"
                name="endTime"
                label={
                  <>
                    <CustomFormattedMessage id="template.entry.end-time" />
                    <small>*</small>
                  </>
                }
              />
            </FormRow>

            <FormRow>
              <InputSelectController<number>
                name="minutes"
                label={
                  <>
                    <CustomFormattedMessage id="template.entry.minutes" />
                    <small>*</small>
                  </>
                }
                options={entryTimeData}
              />
            </FormRow>
          </FormColumns>

          <FormColumns>
            <FormRow>
              <InputFieldController
                name="name"
                label={
                  <>
                    <CustomFormattedMessage id="common.description" />
                    <small>*</small>
                  </>
                }
              />
            </FormRow>

            {type === TemplateEntryType.AppointmentBlock && (
              <FormRow>
                <InputFieldController
                  type="number"
                  name="appointments"
                  label={
                    <>
                      <CustomFormattedMessage id="template.entry.appointments" />
                      <small>*</small>
                    </>
                  }
                  disabled
                />
              </FormRow>
            )}
          </FormColumns>
        </div>
        <div className="template-entry-form__action">
          <Button
            type={ButtonType.TRANSPARENT}
            icon={
              validateTemplateEntryItemsLoading
                ? ButtonIcon.LOADING
                : ButtonIcon.PLUS
            }
            disabled={!formIsValid || validateTemplateEntryItemsLoading}
            onClick={onHandleAdd}
          />
        </div>
      </div>

      {filter(form.formState.errors, (_option, key) =>
        ['Entries', 'Appointment', 'Exception'].includes(key)
      ).map(option => (
        <Message
          key={option?.message}
          type={MessageType.ERROR}
          message={option?.message}
        />
      ))}
    </FormProvider>
  );
}
